import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Trait } from '@/api';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import { TranslatableInputText } from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import { useShowToast } from '@/design_system/Toast';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCheck from '@/icons/Check.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import { useBrands } from '@/models/brand';
import {
  ConditionFormFromGet,
  useDeleteConditionFormQuestion,
  useReorderConditionFormQuestions,
  useUpdateConditionFormQuestion,
} from '@/models/conditionForm';
import { useQuestionTypeOptions } from '@/routes/Settings/ConditionForms/ConditionFormEditor';
import { getDefaultConfig } from '@/routes/Settings/ConditionForms/QuestionConfigEditor';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import { trimI18nWording } from '@/utils/trimI18nWording';

import { useSettingsScope } from '../../../../../../SettingsContext';

import { BrandSearchSelect } from './components/BrandSearchSelect';
import { BrandsReadOnly } from './components/BrandsReadonly';
import { ProductAttributesReadOnly } from './components/ProductAttributesReadonly';
import {
  ProductAttributeValue,
  ProductAttributeValuesInput,
} from './components/ProductAttributeValuesInput';
import { QuestionTypeRadioGroup } from './components/QuestionTypeRadioGroup';
import { ServiceTypesInput } from './components/ServiceTypesInput';
import { ServiceTypesReadonly } from './components/ServiceTypesReadonly';
import { TraitsReadonly } from './components/TraitsReadonly';
import { ConditionQuestionAnswers } from './ConditionQuestionAnswers';
import {
  type IMultiQuantifiableSelectConfig,
  MultiQuantifiableSelectConfig,
} from './MultiQuantifiableSelectConfig';
import { type ITextAndSelectConfig, TextAndSelectConfig } from './TextAndSelectConfig';
import { TraitsCheckboxes } from './TraitsCheckboxes';

const { block, element } = createBEMClasses('settings__condition-form__question');

export type ConditionFormQuestion =
  | ConditionFormFromGet['questions'][number]
  | Exclude<ConditionFormFromGet['questions'][number]['answers'][number]['subQuestion'], null>;

export const ConditionQuestion = ({
  form,
  question,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <ConditionQuestionEditMode
        form={form}
        question={question}
        onClose={() => setIsEditing(false)}
      />
    );
  }

  return (
    <ConditionQuestionReadOnly
      form={form}
      question={question}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onEdit={() => {
        setIsEditing(true);
        setIsOpen(true);
      }}
    />
  );
};

const ConditionQuestionReadOnly = ({
  form,
  question,
  isOpen,
  setIsOpen,
  onEdit,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onEdit: () => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const showToast = useShowToast();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const {
    mutateAsync: deleteConditionFormQuestion,
    isPending: isDeleteConditionFormQuestionPending,
  } = useDeleteConditionFormQuestion();
  const {
    mutateAsync: reorderConditionFormQuestions,
    isPending: isReorderConditionFormQuestionsPending,
  } = useReorderConditionFormQuestions();

  const sortedTopLevelQuestions = useMemo(
    () =>
      [...form.questions].sort(
        (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
      ),
    [form.questions]
  );

  const questionIndex = sortedTopLevelQuestions.findIndex(
    (topLevelQuestion) => topLevelQuestion.id === question.id
  );
  const canReorder = questionIndex !== -1;

  const questionTypeOptions = useQuestionTypeOptions();
  const questionTypeLabel = questionTypeOptions.find(
    (option) => option.value === question.type
  )?.label;

  const questionTitle = question.title ? _db(question.title) : '';

  const handleMove = async (direction: 'up' | 'down') => {
    if (!canReorder) {
      return;
    }

    const targetIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;

    if (targetIndex < 0 || targetIndex >= sortedTopLevelQuestions.length) {
      return;
    }

    const questionIds = sortedTopLevelQuestions.map((topLevelQuestion) => topLevelQuestion.id);

    [questionIds[questionIndex], questionIds[targetIndex]] = [
      questionIds[targetIndex],
      questionIds[questionIndex],
    ];

    try {
      await reorderConditionFormQuestions({
        conditionFormId: form.id,
        questionIds,
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.questions.reorder.error">
            Could not reorder questions. Please try again.
          </Trans>
        ),
        type: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteConditionFormQuestion({
        conditionFormId: form.id,
        questionId: question.id,
      });
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.delete.success">
            &quot;{questionTitle}&quot; has been deleted.
          </Trans>
        ),
        type: 'success',
      });
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.delete.error">
            Could not delete &quot;{questionTitle}&quot;. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Delete failed');
    }
  };

  return (
    <>
      <div className={block({ isOpen })}>
        <div className={element('header-row')}>
          <Button
            variant="style-less"
            className={element('trigger')}
            onPress={() => setIsOpen(!isOpen)}
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.toggle',
              message: `Toggle question ${questionTitle}`,
            })}
          >
            <Stack
              row
              alignItems="center"
              gap="0.5rem"
              flexWrap="nowrap"
              className={element('header')}
            >
              <IconChevron down={!isOpen} up={isOpen} />

              <Paragraph weight="medium" className={element('title')}>
                {questionTitle}
              </Paragraph>

              {questionTypeLabel && (
                <Badge
                  size="x-small"
                  color={
                    (
                      {
                        radio: 'primary',
                        checkbox: 'secondary',
                        'multi-quantifiable-select': 'warning',
                        'text-and-select': 'tertiary',
                      } as const
                    )[question.type]
                  }
                  hasBorder
                >
                  {questionTypeLabel}
                </Badge>
              )}
            </Stack>
          </Button>

          {canReorder && (
            <>
              <Button
                variant="style-less"
                className={element('header__button', { move: true })}
                iconOnly
                disabled={isReorderConditionFormQuestionsPending || questionIndex === 0}
                ariaLabel={t({
                  id: 'settings.organization.data-catalogs.condition-forms.detail.question.move-up.aria',
                  message: `Move question ${questionTitle} up`,
                })}
                onPress={() => handleMove('up')}
              >
                <IconChevron up />
              </Button>

              <Button
                variant="style-less"
                className={element('header__button', { move: true })}
                iconOnly
                disabled={
                  isReorderConditionFormQuestionsPending ||
                  questionIndex === sortedTopLevelQuestions.length - 1
                }
                ariaLabel={t({
                  id: 'settings.organization.data-catalogs.condition-forms.detail.question.move-down.aria',
                  message: `Move question ${questionTitle} down`,
                })}
                onPress={() => handleMove('down')}
              >
                <IconChevron down />
              </Button>
            </>
          )}

          <Button
            variant="style-less"
            className={element('header__button', { edit: true })}
            iconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.edit.aria',
              message: `Edit question ${questionTitle}`,
            })}
            onPress={() => onEdit()}
          >
            <IconEdit />
          </Button>

          <Button
            variant="style-less"
            className={element('header__button', { delete: true })}
            iconOnly
            ariaLabel={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.delete.aria',
              message: `Delete question ${questionTitle}`,
            })}
            onPress={() => setShowDeleteConfirmation(true)}
          >
            <IconTrash />
          </Button>
        </div>

        {isOpen && (
          <>
            <Stack gap="0.5rem" className={element('content')}>
              <div className={element('content__params')}>
                <ProductAttributesReadOnly question={question} />
                {form.type === 'quality_control' && <TraitsReadonly question={question} />}
                {form.type === 'quality_control' && <ServiceTypesReadonly question={question} />}
                <BrandsReadOnly question={question} />
              </div>
            </Stack>

            {question.type === 'text-and-select' ? (
              <ConditionQuestionTextAndSelect question={question} />
            ) : (
              <ConditionQuestionAnswers form={form} question={question} />
            )}
          </>
        )}
      </div>

      <ConfirmationDialog
        title={
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.delete.title">
            Delete question
          </Trans>
        }
        confirm={
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.delete.confirm">
            Delete
          </Trans>
        }
        showDialog={showDeleteConfirmation}
        setShowDialog={setShowDeleteConfirmation}
        isPending={isDeleteConditionFormQuestionPending}
        onConfirm={handleConfirmDelete}
        maxWidth="35rem"
      >
        <Paragraph>
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.delete.description">
            Are you sure you want to delete &quot;{questionTitle}&quot; and all its associated
            answers?
          </Trans>
        </Paragraph>
      </ConfirmationDialog>
    </>
  );
};

const ConditionQuestionEditMode = ({
  form,
  question,
  onClose,
}: {
  form: ConditionFormFromGet;
  question: ConditionFormQuestion;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const showToast = useShowToast();
  const {
    mutateAsync: updateConditionFormQuestion,
    isPending: isUpdateConditionFormQuestionPending,
  } = useUpdateConditionFormQuestion();

  const { scope } = useSettingsScope();

  const { data: { brands: allBrands } = { brands: [] } } = useBrands({
    limit: 2,
    offset: 0,
  });

  const isMultiBrand = allBrands.length > 1;

  const [title, setTitle] = useState(question.title ?? { en: '', fr: '' });
  const [type, setType] = useState(question.type);
  const [productAttributeValues, setProductAttributeValues] = useState<ProductAttributeValue[]>(
    question.productAttributeValues
  );
  const [brands, setBrands] = useState<{ id: string; name: string }[]>(question.brands);
  const [traits, setTraits] = useState<Trait[]>(question.traits);
  const [serviceChoices, setServiceChoices] = useState(question.serviceChoices);
  const [config, setConfig] = useState(question.config ?? getDefaultConfig(question.type) ?? {});

  const titleLocalized = question.title ? _db(question.title) : '';

  const handleTypeChange = (newType: ConditionFormQuestion['type']) => {
    setType(newType);
    setConfig(getDefaultConfig(newType) ?? {});
  };

  const handleSave = async () => {
    try {
      await updateConditionFormQuestion({
        conditionFormId: form.id,
        questionId: question.id,
        title: trimI18nWording(title),
        type,
        productAttributeValues: productAttributeValues.map(({ id }) => ({ id })),
        brands: brands.map(({ id }) => id),
        traits,
        serviceChoices,
        config: type === 'radio' || type === 'checkbox' ? null : config,
      });
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.save.success">
            &quot;{_db(title)}&quot; has been saved.
          </Trans>
        ),
        type: 'success',
      });
      onClose();
    } catch {
      showToast({
        text: (
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.save.error">
            Could not save &quot;{_db(title)}&quot;. Please try again.
          </Trans>
        ),
        type: 'error',
      });

      throw new Error('Save failed');
    }
  };

  return (
    <div className={block({ isOpen: true, isEditing: true })}>
      <div className={element('header-row')}>
        <Stack row alignItems="center" gap="0.5rem" flexWrap="nowrap" className={element('header')}>
          <TranslatableInputText
            size="small"
            value={title}
            onChange={setTitle}
            className={element('header__input')}
            aria-label={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.title.aria',
              message: `Question title for ${titleLocalized}`,
            })}
            placeholder={t({
              id: 'settings.organization.data-catalogs.condition-forms.detail.question.title.placeholder',
              message: 'Title of the question',
            })}
          />
        </Stack>

        <Button
          variant="style-less"
          className={element('header__button', { save: true })}
          iconOnly
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.save.aria',
            message: `Save question ${titleLocalized}`,
          })}
          isLoading={isUpdateConditionFormQuestionPending}
          onPress={handleSave}
        >
          <IconCheck />
        </Button>
        <Button
          variant="style-less"
          className={element('header__button', { cancel: true })}
          iconOnly
          ariaLabel={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.cancel.aria',
            message: `Cancel editing question ${titleLocalized}`,
          })}
          onPress={() => onClose()}
        >
          <IconCross />
        </Button>
      </div>

      <Stack gap="0.5rem" className={element('content')}>
        <QuestionTypeRadioGroup type={type} onChange={handleTypeChange} />

        <ProductAttributeValuesInput
          organizationId={scope!.organizationId}
          selectedProductAttributeValues={productAttributeValues}
          setSelectedProductAttributeValues={setProductAttributeValues}
        />

        {isMultiBrand && <BrandSearchSelect brands={brands} onChange={setBrands} />}

        {form.type === 'quality_control' && (
          <TraitsCheckboxes traits={traits} onChange={setTraits} />
        )}

        {form.type === 'quality_control' && (
          <ServiceTypesInput
            selectedServiceChoices={serviceChoices}
            setSelectedServiceChoices={setServiceChoices}
          />
        )}

        {type === 'multi-quantifiable-select' && (
          <MultiQuantifiableSelectConfig
            config={config as IMultiQuantifiableSelectConfig}
            onChange={setConfig}
          />
        )}

        {type === 'text-and-select' && (
          <TextAndSelectConfig config={config as ITextAndSelectConfig} onChange={setConfig} />
        )}
      </Stack>

      {type !== 'text-and-select' && <ConditionQuestionAnswers form={form} question={question} />}
    </div>
  );
};

const ConditionQuestionTextAndSelect = ({ question }: { question: ConditionFormQuestion }) => {
  const { _db } = useDatabaseTranslation();

  if (question.type !== 'text-and-select' || !question.config || !('input' in question.config)) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      <Paragraph weight="medium" style={{ padding: '0 1rem' }}>
        <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.answer">
          Answer:
        </Trans>
      </Paragraph>
      <div className={element('text-and-select')}>
        <Stack gap="0.25rem">
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.text">
              Text label:
            </Trans>{' '}
            <InlineText weight="medium">"{_db(question.config.input.label)}"</InlineText>
          </Paragraph>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.text.placeholder">
              Text placeholder:
            </Trans>{' '}
            <InlineText weight="medium">"{_db(question.config.input.placeholder)}"</InlineText>
          </Paragraph>
        </Stack>
        <Stack gap="0.25rem">
          <Stack>
            <Paragraph>
              <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select">
                Options:
              </Trans>
            </Paragraph>
            <ul>
              {question.config.select.options.map((option) => (
                <li key={option.value}>
                  <Paragraph size="200">{_db(option.label)}</Paragraph>
                </li>
              ))}
            </ul>
          </Stack>
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select.placeholder">
              Options placeholder:
            </Trans>{' '}
            <InlineText weight="medium">"{_db(question.config.select.placeholder)}"</InlineText>
          </Paragraph>
        </Stack>
      </div>
    </Stack>
  );
};
